import React from 'react';
import { Helmet } from 'react-helmet-async';
import { distributor } from '../../data/mock';
import { contactFAQs } from '../../data/approvedContent';

const SEOHead = ({ 
  title = 'XION Medical Philippines',
  description = 'XION Medical endoscopy systems in the Philippines. Exclusive distributor: FMSInc. Premium German-engineered medical devices for ENT, surgery, and diagnostics.',
  path = '/',
  type = 'website',
  image = null,
  includeFAQSchema = false
}) => {
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://xion-philippines.com';
  const fullUrl = `${siteUrl}${path}`;
  const fullTitle = title.includes('XION') ? title : `${title} | XION Medical Philippines`;
  // Social previews need an ABSOLUTE image URL; fall back to the logo
  const rawImage = image || '/assets/xion-logo.png';
  const absImage = rawImage.startsWith('http') ? rawImage : `${siteUrl}${rawImage}`;

  // Organization Schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'XION Medical (Philippines)',
    url: siteUrl,
    logo: `${siteUrl}/assets/xion-logo.png`,
    description: 'XION Medical endoscopy systems - Exclusive Philippines distribution by FMSInc.',
    foundingDate: '1991',
    sameAs: [
      'https://www.xion-medical.com/en/',
      distributor.website
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'sales',
      areaServed: 'PH',
      availableLanguage: ['en', 'fil']
    },
    // Distributor as subsidiary/partner
    member: {
      '@type': 'Organization',
      name: distributor.name,
      url: distributor.website,
      description: 'Exclusive distributor of XION Medical products in the Philippines'
    }
  };

  // FAQ Schema (for contact page)
  const faqSchema = includeFAQSchema ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: contactFAQs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  } : null;

  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="geo.region" content="PH" />
      <meta name="geo.country" content="Philippines" />

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content="XION Medical Philippines" />
      <meta property="og:locale" content="en_PH" />
      <meta property="og:image" content={absImage} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absImage} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
